import React from 'react';
import { Link } from 'react-router-dom';
import { Box } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useFavicon } from '../useFavicon';   
import './MainPage.css'; // We'll create this CSS file

// Define the favicon for MainPage
const MAIN_FAVICON_URL = process.env.PUBLIC_URL + "/music.svg";

const MainPage = () => {
  const { hasPermission } = useAuth();
  useFavicon(MAIN_FAVICON_URL); // Set the favicon for this page
  
  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      <div className="main-page">
        <header className="main-header">
          <h1 className="main-title">🎵 Media & Music Hub</h1>
          <p className="main-subtitle">Your complete multimedia management and playback center</p>
        </header>

        <div className="features-grid">
          {/* Music & MIDI Section */}
          <section className="feature-section music-section">
            <h2 className="section-title">🎼 Music & MIDI</h2>
            <p className="section-description">Create, manage, and play MIDI files with advanced audio features</p>
            <div className="feature-links">
              <Link to="/add-midi" className="feature-link">
                <span className="icon">📁</span>
                <div className="link-content">
                  <h3>Add MIDI Files</h3>
                  <p>Upload and organize your MIDI compositions</p>
                </div>
              </Link>
              <Link to="/midi-player" className="feature-link">
                <span className="icon">▶️</span>
                <div className="link-content">
                  <h3>MIDI Player</h3>
                  <p>Play MIDI files with customizable instruments</p>
                </div>
              </Link>
              <Link to="/midi-player-piano" className="feature-link">
                <span className="icon">🎹</span>
                <div className="link-content">
                  <h3>Piano Player</h3>
                  <p>Interactive piano visualization for MIDI playback</p>
                </div>
              </Link>
            </div>
          </section>

          {/* Media Management Section */}
          <section className="feature-section media-section">
            <h2 className="section-title">📱 Media Management</h2>
            <p className="section-description">Upload, organize, and view your media files</p>
            <div className="feature-links">
              <Link to="/media-files" className="feature-link">
                <span className="icon">📂</span>
                <div className="link-content">
                  <h3>Media Files</h3>
                  <p>Upload and manage your media collection</p>
                </div>
              </Link>
              <Link to="/image-upload" className="feature-link primary">
                <span className="icon">📸</span>
                <div className="link-content">
                  <h3>Image Upload & Analysis</h3>
                  <p>Upload images with EXIF/GPS analysis and AI vision insights</p>
                </div>
              </Link>
              <Link to="/view-media" className="feature-link">
                <span className="icon">🖼️</span>
                <div className="link-content">
                  <h3>View Media</h3>
                  <p>Browse and preview your uploaded media</p>
                </div>
              </Link>
            </div>
          </section>

          {/* YouTube & Video Section */}
          <section className="feature-section video-section">
            <h2 className="section-title">📺 Video & YouTube</h2>
            <p className="section-description">Manage and display YouTube videos and links</p>
            <div className="feature-links">
              <div style={{ width: '100%', marginBottom: '20px' }}>
                <h4 style={{ color: '#1976d2', fontSize: '0.9rem', marginBottom: '10px', paddingLeft: '10px', borderLeft: '4px solid #1976d2' }}>Tonality YouTube Videos</h4>
                <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                  <Link to="/tonality-add-youtube" className="feature-link" style={{ flex: '1 1 calc(50% - 10px)', minWidth: '280px', backgroundColor: '#e3f2fd' }}>
                    <span className="icon">🎬</span>
                    <div className="link-content">
                      <h3>Add Tonality YouTube Videos</h3>
                      <p>Add YouTube videos with tonality analysis</p>
                    </div>
                  </Link>
                  <Link to="/tonality-youtube-video" className="feature-link" style={{ flex: '1 1 calc(50% - 10px)', minWidth: '280px', backgroundColor: '#e3f2fd' }}>
                    <span className="icon">📽️</span>
                    <div className="link-content">
                      <h3>Display Tonality Video</h3>
                      <p>Watch videos with tonality visualization</p>
                    </div>
                  </Link>
                </div>
              </div>
              <div style={{ width: '100%' }}>
                <h4 style={{ color: '#388e3c', fontSize: '0.9rem', marginBottom: '10px', paddingLeft: '10px', borderLeft: '4px solid #388e3c' }}>Custom YouTube Videos</h4>
                <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                  <Link to="/custom-youtube-videos" className="feature-link" style={{ flex: '1 1 calc(50% - 10px)', minWidth: '280px', backgroundColor: '#e8f5e9' }}>
                    <span className="icon">🎬</span>
                    <div className="link-content">
                      <h3>Custom YouTube Player</h3>
                      <p>Watch videos with custom playback controls</p>
                    </div>
                  </Link>
                  {hasPermission('admin') && (
                    <Link to="/admin/custom-youtube" className="feature-link admin" style={{ flex: '1 1 calc(50% - 10px)', minWidth: '280px', backgroundColor: '#c8e6c9' }}>
                      <span className="icon">⚙️</span>
                      <div className="link-content">
                        <h3>Manage Custom Videos</h3>
                        <p>Admin panel for custom YouTube videos with QR codes</p>
                      </div>
                    </Link>
                  )}
                </div>
              </div>
              <div style={{ width: '100%', marginTop: '20px' }}>
                <h4 style={{ color: '#f57c00', fontSize: '0.9rem', marginBottom: '10px', paddingLeft: '10px', borderLeft: '4px solid #f57c00' }}>Book Links</h4>
                <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                  <Link to="/add-links" className="feature-link" style={{ flex: '1 1 calc(50% - 10px)', minWidth: '280px', backgroundColor: '#fff3e0' }}>
                    <span className="icon">🔗</span>
                    <div className="link-content">
                      <h3>Manage Book Links</h3>
                      <p>Organize and categorize your book links</p>
                    </div>
                  </Link>
                </div>
              </div>
            </div>
          </section>

          {/* System & Diagnostics Section */}
          <section className="feature-section system-section">
            <h2 className="section-title">🔧 System & Diagnostics</h2>
            <p className="section-description">Monitor performance and test system connections</p>
            <div className="feature-links">
              <Link to="/cors-test" className="feature-link">
                <span className="icon">🌐</span>
                <div className="link-content">
                  <h3>API Connection Test</h3>
                  <p>Test backend connectivity and CORS settings</p>
                </div>
              </Link>
              <Link to="/app-insights-test" className="feature-link primary">
                <span className="icon">📊</span>
                <div className="link-content">
                  <h3>Application Insights</h3>
                  <p>Monitor application performance and usage</p>
                </div>
              </Link>
              <Link to="/app-insights-diagnostic" className="feature-link warning">
                <span className="icon">🔍</span>
                <div className="link-content">
                  <h3>Diagnostics</h3>
                  <p>Advanced debugging and troubleshooting tools</p>
                </div>
              </Link>
            </div>
          </section>
        </div>

        <footer className="main-footer">
          <p>🚀 Powered by Azure Container Apps | 🔒 Secure & Scalable</p>
          <p style={{ marginTop: '8px', fontSize: '0.85rem' }}>
            <a href="/privacy-policy" style={{ color: 'inherit', marginRight: '16px' }}>Privacy Policy</a>
            <a href="/terms-of-service" style={{ color: 'inherit' }}>Terms of Service</a>
          </p>
        </footer>
      </div>
    </Box>
  );
};

export default MainPage;